/**
 * Static fallback for `GET /permissions/feature-catalog` (MV2-199).
 *
 * Mirrors `app/services/feature_catalog.py` so the new
 * `<PolicyEditor>` renders sensibly even when:
 *   * the bundle UI ships before backend PR #221 is deployed; or
 *   * the request is in-flight on slow connections; or
 *   * the endpoint 4xx/5xxs and the editor would otherwise hard-error.
 *
 * Drift-safe: the backend is the source of truth at runtime.
 * `usePermissionsCatalog` uses this as `placeholderData` so a successful
 * fetch overrides instantly. Update this file in the same PR whenever
 * the backend catalog changes — the deviation only matters during the
 * placeholder window.
 */

import type { PermissionsCatalog } from "@/api/admin";

export const PERMISSIONS_FALLBACK: PermissionsCatalog = {
  features: [
    // Overview
    {
      key: "view_overview_graph",
      label: "Fleet overview chart",
      description: "Headline generation/load chart on the dashboard.",
      group: "overview",
      also_gates: [],
    },
    {
      key: "view_revenue",
      label: "Revenue (lifetime)",
      description: "Lifetime revenue tile + revenue series in reports.",
      group: "overview",
      also_gates: ["Site Detail Analytics", "Reports"],
    },
    {
      key: "view_revenue_today",
      label: "Revenue (today)",
      description: "Today's revenue tile on the overview.",
      group: "overview",
      also_gates: [],
    },
    {
      key: "view_co2_saved",
      label: "CO₂ avoided",
      description: "CO₂ avoided KPI tile.",
      group: "overview",
      also_gates: [],
    },
    {
      key: "view_trees_planted",
      label: "Trees equivalent",
      description: "Trees-equivalent sustainability tile.",
      group: "overview",
      also_gates: [],
    },
    {
      key: "view_curtailment",
      label: "Curtailment",
      description: "Curtailment KPI and chart series.",
      group: "overview",
      also_gates: ["Site Detail Analytics"],
    },
    {
      key: "view_historical_data",
      label: "Historical data",
      description: "Long-range historical series on charts.",
      group: "overview",
      also_gates: ["Site Detail Analytics", "Reports"],
    },
    {
      key: "view_map",
      label: "Map view",
      description: "Geographic map of sites.",
      group: "overview",
      also_gates: [],
    },
    // Sites
    {
      key: "view_site",
      label: "Site detail",
      description: "Open a site's detail page (Overview, Devices, etc.).",
      group: "sites",
      also_gates: [],
    },
    {
      key: "view_site_install_info",
      label: "Site install info",
      description: "Capacity, install date, asset inventory metadata.",
      group: "sites",
      also_gates: [],
    },
    {
      key: "view_site_devices",
      label: "Site devices",
      description: "Per-device status, registers, and configuration.",
      group: "sites",
      also_gates: [],
    },
    // Alarms
    {
      key: "view_live_alarms",
      label: "Live alarms",
      description: "Currently active alarm queue.",
      group: "alarms",
      also_gates: [],
    },
    {
      key: "view_historical_alarms",
      label: "Historical alarms",
      description: "Resolved/cleared alarm history for forensics.",
      group: "alarms",
      also_gates: [],
    },
    // Tickets
    {
      key: "view_tickets",
      label: "View tickets",
      description: "Read access to the ticket queue.",
      group: "tickets",
      also_gates: [],
    },
    {
      key: "create_tickets",
      label: "Create tickets",
      description: "Open new tickets manually or from alarms.",
      group: "tickets",
      also_gates: [],
    },
    {
      key: "close_tickets",
      label: "Close tickets",
      description: "Resolve and close tickets.",
      group: "tickets",
      also_gates: [],
    },
    // Controls
    {
      key: "send_controls",
      label: "Send commands",
      description: "Issue ad-hoc remote-control commands to devices.",
      group: "controls",
      also_gates: [],
    },
    {
      key: "schedule_bess",
      label: "Schedule BESS",
      description: "Schedule battery charge/discharge windows.",
      group: "controls",
      also_gates: [],
    },
    {
      key: "issue_adhoc_command",
      label: "Issue ad-hoc command",
      description: "Type-driven dispatch modal for arbitrary commands.",
      group: "controls",
      also_gates: [],
    },
    // Files
    {
      key: "download_files",
      label: "View / download",
      description: "Browse the per-site file tree and download files.",
      group: "files",
      also_gates: [],
    },
    {
      key: "upload_files",
      label: "Upload",
      description: "Upload new files into the per-site browser.",
      group: "files",
      also_gates: [],
    },
    {
      key: "delete_files",
      label: "Delete",
      description: "Delete files from the per-site browser.",
      group: "files",
      also_gates: [],
    },
    // Reports
    {
      key: "view_reports",
      label: "View past reports",
      description: "Browse and download previously generated report runs.",
      group: "reports",
      also_gates: [],
    },
    {
      key: "generate_report",
      label: "Generate",
      description: "Generate ad-hoc PDF reports on demand.",
      group: "reports",
      also_gates: [],
    },
    {
      key: "subscribe_report",
      label: "Manage subscriptions",
      description:
        "Create, edit, and pause scheduled report subscriptions and their recipients.",
      group: "reports",
      also_gates: [],
    },
    // ML
    {
      key: "view_ml",
      label: "View ML / forecast",
      description: "Read the ML model registry and accuracy metrics.",
      group: "ml",
      also_gates: [],
    },
    {
      key: "view_forecast",
      label: "Forecast charts",
      description: "Per-site forecast vs actual chart and tables.",
      group: "ml",
      also_gates: ["Overview tiles"],
    },
    // trigger_retrain intentionally omitted — SuperAdmin-only feature.
    // Admin
    {
      key: "manage_users",
      label: "Users",
      description: "Invite, edit, and deactivate users in this tenant.",
      group: "admin",
      also_gates: [],
    },
    {
      key: "manage_policies",
      label: "Policies",
      description: "Create and edit access policies.",
      group: "admin",
      also_gates: [],
    },
    {
      key: "manage_tariffs",
      label: "Tariffs",
      description: "Edit per-site import/export tariff schedules.",
      group: "admin",
      also_gates: [],
    },
    {
      key: "manage_branding",
      label: "Branding",
      description: "Tenant logo, colours, custom-domain branding.",
      group: "admin",
      also_gates: [],
    },
    // manage_escalation_rules + manage_webhooks intentionally omitted —
    // backend marks them as DEFERRED_FEATURES (no UI surface yet).
    // Cross-client
    {
      key: "invite_cross_client",
      label: "Invite cross-client",
      description: "Invite another tenant to access selected sites.",
      group: "cross_client",
      also_gates: [],
    },
    {
      key: "accept_cross_client",
      label: "Accept cross-client",
      description: "Accept incoming cross-tenant access invitations.",
      group: "cross_client",
      also_gates: [],
    },
  ],
  groups: [
    {
      key: "overview",
      label: "Overview",
      icon: "LayoutDashboard",
      order: 1,
      description: "Dashboard tiles and the fleet overview chart.",
    },
    {
      key: "sites",
      label: "Sites",
      icon: "Building2",
      order: 2,
      description: "Site detail surfaces — devices, configuration, files.",
    },
    {
      key: "alarms",
      label: "Alarms",
      icon: "AlertTriangle",
      order: 3,
      description: "Live alarm queue and historical-alarm forensic view.",
    },
    {
      key: "tickets",
      label: "Tickets",
      icon: "Ticket",
      order: 4,
      description: "Ticket queue and per-ticket actions.",
    },
    {
      key: "controls",
      label: "Controls",
      icon: "Sliders",
      order: 5,
      description: "Direct device commands and BESS scheduling.",
    },
    {
      key: "files",
      label: "Files",
      icon: "Folder",
      order: 6,
      description: "Per-site file browser — view, upload, delete.",
    },
    {
      key: "reports",
      label: "Reports",
      icon: "FileText",
      order: 7,
      description: "Generate ad-hoc reports and manage subscriptions.",
    },
    {
      key: "ml",
      label: "ML / Forecast",
      icon: "Sparkles",
      order: 8,
      description: "Forecast charts and retrain controls.",
    },
    {
      key: "admin",
      label: "Admin",
      icon: "Settings",
      order: 9,
      description: "Tenant admin — users, policies, branding, integrations.",
    },
    {
      key: "cross_client",
      label: "Cross-client",
      icon: "Users",
      order: 10,
      description: "Cross-tenant access invitations.",
    },
  ],
  persona_rules: {
    om_signature: [
      "close_tickets",
      "create_tickets",
      "send_controls",
      "view_historical_alarms",
      "view_site_devices",
      "view_tickets",
    ],
  },
  persona_classes: ["SuperAdmin", "ClientAdmin", "OM", "SiteOwner"],
};
